import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, ArrowRight, Warning, Eye, EyeSlash } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords don't match");
      return;
    }
    if (password.length < 8 || !/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
      setErr("Password must be 8+ characters with at least one letter and one number");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      // Auto-logged in via cookie
      await refresh?.();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reset failed. Link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center p-6" data-testid="reset-no-token">
        <div className="max-w-md text-center">
          <Warning size={28} weight="fill" className="text-neg mx-auto mb-3" />
          <h1 className="text-2xl font-medium text-primary-fg mb-2">Missing reset token</h1>
          <p className="text-sm text-secondary-fg mb-6">
            This page should be opened from the link in your password-reset email.
          </p>
          <Link to="/forgot-password" className="text-pos hover:underline">
            Request a new reset link →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app grain flex flex-col" data-testid="reset-password-page">
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Link
            to="/login"
            className="mb-8 flex items-center gap-2 text-muted-fg hover:text-primary-fg text-xs font-mono uppercase tracking-widest"
          >
            <ArrowLeft size={14} /> Back to sign in
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <Logo size={32} />
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-muted-fg">
              Breakout Scanner
            </div>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos mb-2">
            /// Set new password
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-primary-fg mb-2">
            Choose a new password
          </h1>
          <p className="text-sm text-secondary-fg mb-8">
            Pick something strong — minimum 8 characters with at least one letter and one number.
          </p>

          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                New password
              </span>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
                <input
                  data-testid="reset-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  className="w-full pl-9 pr-10 py-2.5 bg-surface border border-subtle rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus transition font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  data-testid="reset-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-fg hover:text-pos transition rounded-sm"
                >
                  {showPassword ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
            </label>

            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                Confirm password
              </span>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
                <input
                  data-testid="reset-confirm-input"
                  type={showPassword ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  className="w-full pl-9 pr-3 py-2.5 bg-surface border border-subtle rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus transition font-mono text-sm"
                />
              </div>
            </label>

            {err && (
              <div
                data-testid="reset-error"
                className="text-neg text-sm bg-neg-soft border border-neg-soft px-3 py-2 rounded-sm"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full mt-2 px-4 py-3 rounded-sm font-medium hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
            >
              {busy ? "Updating…" : "Update password & sign in"}
              {!busy && <ArrowRight size={16} weight="bold" />}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
